import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { notifications } from '@mantine/notifications';
import type { OrganizationMember, OrganizationInvite } from '@/types/models';

// Fetch all members of an organization
async function fetchOrganizationMembers(organizationId: string): Promise<OrganizationMember[]> {
  const { data, error } = await supabase
    .from('organization_members')
    .select(`
      *,
      profile:profiles(*)
    `)
    .eq('organization_id', organizationId)
    .order('joined_at', { ascending: false });

  if (error) throw error;

  return data || [];
}

// Fetch pending invites for an organization
async function fetchOrganizationInvites(organizationId: string): Promise<OrganizationInvite[]> {
  const { data, error } = await supabase
    .from('organization_invites')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('status', 'pending')
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false });

  if (error) throw error;

  return data || [];
}

// Fetch invites for the current user (by email)
async function fetchMyInvites(userEmail: string): Promise<OrganizationInvite[]> {
  const { data, error } = await supabase
    .from('organization_invites')
    .select(`
      *,
      organization:organizations(*)
    `)
    .eq('email', userEmail)
    .eq('status', 'pending')
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false});

  if (error) throw error;

  return data || [];
}

// Fetch a single invite by token (for accepting)
// Uses secure RPC function to prevent listing all invites
async function fetchInviteByToken(token: string): Promise<OrganizationInvite | null> {
  const { data, error } = await supabase
    .rpc('get_invite_by_token', { invite_token: token });

  if (error) {
    console.error('Error fetching invite:', error);
    return null;
  }

  if (!data || data.length === 0) {
    return null;
  }

  // Transform RPC result to match expected shape
  const invite = data[0];
  return {
    ...invite,
    organization: invite.organization_name ? { name: invite.organization_name } : null,
  } as OrganizationInvite;
}

// Update member role
async function updateMemberRole(memberId: string, role: 'admin' | 'manager' | 'member'): Promise<void> {
  const { error } = await supabase
    .from('organization_members')
    .update({ role })
    .eq('id', memberId);

  if (error) throw error;
}

// Toggle admin status for a member
async function toggleAdminStatus(memberId: string, isAdmin: boolean): Promise<void> {
  const { error } = await supabase
    .from('organization_members')
    .update({ is_admin: isAdmin })
    .eq('id', memberId);

  if (error) throw error;
}

// Remove a member from organization
async function removeMember(memberId: string): Promise<void> {
  const { error } = await supabase
    .from('organization_members')
    .delete()
    .eq('id', memberId);

  if (error) throw error;
}

// Create an invite (token-based, secure)
async function createInvite(
  organizationId: string,
  email: string,
  isAdmin: boolean,
  customRoleId: string | null,
  invitedBy: string
): Promise<OrganizationInvite> {
  const { data, error } = await supabase
    .from('organization_invites')
    .insert({
      organization_id: organizationId,
      email: email.toLowerCase(),
      is_admin: isAdmin,
      custom_role_id: customRoleId,
      role: isAdmin ? 'admin' : 'member', // Keep for backward compatibility
      invited_by: invitedBy,
    })
    .select()
    .single();

  if (error) throw error;

  // Send invitation email via Supabase Edge Function
  try {
    const { error: emailError } = await supabase.functions.invoke('send-invitation-email', {
      body: {
        invitationId: data.id,
        email: email.toLowerCase(),
        organizationId,
        role: isAdmin ? 'admin' : 'member',
        invitedBy,
      },
    });

    if (emailError) {
      console.error('Failed to send invitation email:', emailError);
      // Don't throw - we still want to return the invite even if email fails
    }
  } catch (emailError) {
    console.error('Error invoking send-invitation-email function:', emailError);
    // Don't throw - we still want to return the invite even if email fails
  }

  return data;
}

// Accept an invite using token
async function acceptInvite(token: string): Promise<OrganizationMember> {
  const { data, error } = await supabase
    .rpc('accept_org_invite', { invite_token: token });

  if (error) throw error;
  return data;
}

// Revoke/cancel an invite
async function revokeInvite(inviteId: string): Promise<void> {
  const { error } = await supabase
    .rpc('revoke_org_invite', { invite_id: inviteId });

  if (error) throw error;
}

// React Query Hooks

export function useOrganizationMembers(organizationId: string) {
  return useQuery({
    queryKey: ['organization-members', organizationId],
    queryFn: () => fetchOrganizationMembers(organizationId),
    enabled: !!organizationId,
  });
}

export function useOrganizationInvites(organizationId: string) {
  return useQuery({
    queryKey: ['organization-invites', organizationId],
    queryFn: () => fetchOrganizationInvites(organizationId),
    enabled: !!organizationId,
  });
}

export function useMyInvites(userEmail: string) {
  return useQuery({
    queryKey: ['my-invites', userEmail],
    queryFn: () => fetchMyInvites(userEmail),
    enabled: !!userEmail,
  });
}

export function useInviteByToken(token: string) {
  return useQuery({
    queryKey: ['invite-by-token', token],
    queryFn: () => fetchInviteByToken(token),
    enabled: !!token,
    retry: false, // Don't retry if invite is invalid
  });
}

export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: 'admin' | 'manager' | 'member' }) =>
      updateMemberRole(memberId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
      notifications.show({
        title: 'Role updated',
        message: 'User role has been updated successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update user role. Please try again.',
        color: 'red',
      });
    },
  });
}

export function useToggleAdminStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, isAdmin }: { memberId: string; isAdmin: boolean }) =>
      toggleAdminStatus(memberId, isAdmin),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
      notifications.show({
        title: 'Admin status updated',
        message: 'User admin status has been updated successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update admin status. Please try again.',
        color: 'red',
      });
    },
  });
}

export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (memberId: string) => removeMember(memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
      notifications.show({
        title: 'User removed',
        message: 'User has been removed from the organization',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to remove user. Please try again.',
        color: 'red',
      });
    },
  });
}

export function useCreateInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      email,
      isAdmin,
      customRoleId,
      invitedBy,
    }: {
      organizationId: string;
      email: string;
      isAdmin: boolean;
      customRoleId: string | null;
      invitedBy: string;
    }) => createInvite(organizationId, email, isAdmin, customRoleId, invitedBy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-invites'] });
      notifications.show({
        title: 'Invitation sent',
        message: 'An invitation email has been sent to the user',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to send invite. Please try again.',
        color: 'red',
      });
    },
  });
}

export function useAcceptInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token: string) => acceptInvite(token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
      queryClient.invalidateQueries({ queryKey: ['my-invites'] });
      notifications.show({
        title: 'Invite accepted',
        message: 'You have successfully joined the organization',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to accept invite. Please try again.',
        color: 'red',
      });
    },
  });
}

export function useRevokeInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inviteId: string) => revokeInvite(inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-invites'] });
      notifications.show({
        title: 'Invite revoked',
        message: 'The invitation has been canceled',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to revoke invite. Please try again.',
        color: 'red',
      });
    },
  });
}
